// Customer Paperwork API functions
// Handles upload, retrieval, and management of customer paperwork PDFs

import { getSessionUUID } from '../components/lib/AuthToken'; // Import the utility function
import logger from "../utils/logger";


/**
 * Upload customer paperwork for an order
 * @param {File} file - PDF file to upload
 * @param {number} orderId - Order ID to associate with the paperwork
 * @returns {Promise<Object>} Response with upload status and file info
 */
export const uploadCustomerPaperwork = async (file, orderId) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        const formData = new FormData();
        formData.append('file', file);
        formData.append('order_id', orderId);

        const response = await fetch('/paperwork/upload', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: formData,
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok) {
            return {
                success: true,
                message: responseData.message || 'File uploaded successfully',
                data: responseData,
                status: response.status
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to upload file',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error uploading customer paperwork:', error);
        return {
            success: false,
            message: error.message || 'An error occurred during file upload',
            status: 500
        };
    }
};

/**
 * Upload multiple customer paperwork files for an order (max 3)
 * @param {FileList|Array<File>} files - PDF files to upload (max 3)
 * @param {number} orderId - Order ID to associate with the paperwork
 * @returns {Promise<Object>} Response with upload status and file info
 */
export const uploadMultipleCustomerPaperwork = async (files, orderId) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        if (files.length > 5) {
            throw new Error('Maximum 5 files allowed per order');
        }

        const formData = new FormData();
        
        // Add each file to the form data
        Array.from(files).forEach((file, index) => {
            formData.append(`file${index}`, file);
        });
        
        formData.append('order_id', orderId);

        const response = await fetch('/paperwork/upload/batch', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: formData,
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok || response.status === 207) { // 207 = Multi-status
            return {
                success: true,
                message: responseData.message || 'Files uploaded successfully',
                data: responseData,
                status: response.status,
                hasErrors: response.status === 207
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to upload files',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error uploading multiple customer paperwork:', error);
        return {
            success: false,
            message: error.message || 'An error occurred during file upload',
            status: 500
        };
    }
};

/**
 * Get all paperwork for a specific order
 * @param {number} orderId - Order ID to get paperwork for
 * @returns {Promise<Object>} Response with paperwork list
 */
export const getOrderPaperwork = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        const response = await fetch(`/paperwork/order/${orderId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok) {
            return {
                success: true,
                data: responseData.paperwork || [],
                status: response.status
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to fetch paperwork',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error fetching order paperwork:', error);
        return {
            success: false,
            message: error.message || 'An error occurred while fetching paperwork',
            status: 500
        };
    }
};

/**
 * Get public URL for a paperwork file
 * @param {string} filePath - File path in storage
 * @returns {Promise<Object>} Response with file URL
 */
export const getPaperworkFileUrl = async (filePath) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        const response = await fetch(`/paperwork/file/${filePath}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok) {
            return {
                success: true,
                fileUrl: responseData.file_url,
                status: response.status
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to get file URL',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error getting paperwork file URL:', error);
        return {
            success: false,
            message: error.message || 'An error occurred while getting file URL',
            status: 500
        };
    }
};

/**
 * Delete a paperwork file
 * @param {number} paperworkId - ID of the paperwork to delete
 * @returns {Promise<Object>} Response with deletion status
 */
export const deleteCustomerPaperwork = async (paperworkId) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        const response = await fetch(`/paperwork/${paperworkId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok) {
            return {
                success: true,
                message: responseData.message || 'Paperwork deleted successfully',
                status: response.status
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to delete paperwork',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error deleting customer paperwork:', error);
        return {
            success: false,
            message: error.message || 'An error occurred while deleting paperwork',
            status: 500
        };
    }
};

/**
 * Get all paperwork URLs for an order (for printing)
 * @param {number} orderId - Order ID to get paperwork URLs for
 * @returns {Promise<Object>} Response with paperwork URLs
 */
export const getAllPaperworkUrls = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function for authentication

        const response = await fetch(`/paperwork/order/${orderId}/print-urls`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json',
            },
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            logger.error('Failed to parse JSON response:', jsonError);
            return {
                success: false,
                message: 'Server returned invalid response',
                status: response.status
            };
        }

        if (response.ok) {
            return {
                success: true,
                data: responseData.paperwork_urls || [],
                message: responseData.message || 'Paperwork URLs retrieved successfully',
                status: response.status
            };
        } else {
            return {
                success: false,
                message: responseData.error || 'Failed to get paperwork URLs',
                status: response.status
            };
        }
    } catch (error) {
        logger.error('Error getting paperwork URLs:', error);
        return {
            success: false,
            message: error.message || 'An error occurred while getting paperwork URLs',
            status: 500
        };
    }
};
